import queryString from "query-string";
import { api, authHeader } from "../configs/axiosConfigs";
import {
  buildAppReferralLink,
  buildPartnerJoinLink,
  buildReferralShareText,
} from "../utils/referralTracking";

const BASE = "api/partner/commission";

const NOT_FOUND = 3;
const UNAUTHORIZED = 6;

const isNotFoundStatus = (statusCode) =>
  statusCode === NOT_FOUND ||
  statusCode === "NotFound" ||
  statusCode === "notFound";

const isUnauthorizedStatus = (statusCode) =>
  statusCode === UNAUTHORIZED ||
  statusCode === "UnAuthorized" ||
  statusCode === "Unauthorized";

const partnerLevelMap = {
  Level1: 1,
  Level2: 2,
};

const partnerStatusMap = {
  PendingActivation: 1,
  Active: 2,
  Suspended: 3,
  Inactive: 4,
};

const reverseMap = (map) =>
  Object.entries(map).reduce((acc, [key, value]) => {
    acc[value] = key;
    acc[String(value)] = key;
    return acc;
  }, {});

const partnerLevelNameMap = reverseMap(partnerLevelMap);
const partnerStatusNameMap = reverseMap(partnerStatusMap);

const normalizeEnumName = (value, reverseMap) => {
  if (value == null || value === "") return value;
  if (typeof value === "string") {
    if (reverseMap[value] != null) return value;
    const numeric = Number(value);
    if (Number.isFinite(numeric) && reverseMap[numeric] != null) return reverseMap[numeric];
    return value;
  }
  if (typeof value === "number") {
    return reverseMap[value] != null ? reverseMap[value] : String(value);
  }
  return value;
};

const normalizePartnerEnums = (data) => {
  if (!data || typeof data !== "object") return data;
  return {
    ...data,
    partnerLevel: normalizeEnumName(
      data.partnerLevel ?? data.PartnerLevel,
      partnerLevelNameMap
    ),
    status: normalizeEnumName(data.status ?? data.Status, partnerStatusNameMap),
    canCreateSubPartners: Boolean(data.canCreateSubPartners ?? data.CanCreateSubPartners),
  };
};

const isLevel1Partner = (data) => {
  const level = data?.partnerLevel ?? data?.PartnerLevel ?? "";
  return (
    String(level).toLowerCase() === "level1" ||
    level === 1 ||
    level === "1"
  );
};

const canCreateSubPartners = (data) =>
  Boolean(data.canCreateSubPartners ?? data.CanCreateSubPartners);

/** Partner links always use app domain (/r/{code}, /p/{code}/join) — never portal routes. */
const applyAppReferralLinks = (data) => {
  if (!data || typeof data !== "object") return data;
  const code = String(data.referralCode ?? data.ReferralCode ?? "").trim();
  if (!code) return data;

  const link = buildAppReferralLink(code);
  const shareText = buildReferralShareText(code);
  const apiJoinLink = String(data.partnerJoinLink ?? data.PartnerJoinLink ?? "").trim();
  const showJoinLink =
    apiJoinLink.length > 0 || (isLevel1Partner(data) && canCreateSubPartners(data));
  const partnerJoinLink = showJoinLink ? buildPartnerJoinLink(code) : null;

  return {
    ...data,
    referralLink: link,
    ReferralLink: link,
    shareText,
    ShareText: shareText,
    qrCodePayload: link,
    QrCodePayload: link,
    partnerJoinLink,
    PartnerJoinLink: partnerJoinLink,
  };
};

const request = async (url, method = "GET", data) => {
  try {
    const response = await api.request({
      url,
      method,
      data,
      headers: { ...authHeader(), "Content-Type": "application/json" },
    });
    return response.data;
  } catch (error) {
    const res = error?.response?.data;
    if (res) return res;
    throw error;
  }
};

const normalize = (res, fallbackMessage) => {
  const isSuccess = res?.isSuccess ?? res?.IsSuccess;
  const statusCode = res?.statusCode ?? res?.StatusCode;
  if (!res || isSuccess === false) {
    return {
      error: res?.message || res?.Message || fallbackMessage,
      data: null,
      statusCode,
      noPartnerProfile: isNotFoundStatus(statusCode),
      isUnauthorized: isUnauthorizedStatus(statusCode),
    };
  }

  const rawData = res.data ?? res.Data ?? res;
  let normalizedData = rawData;

  // If data is an array (e.g., team members), normalize each item
  if (Array.isArray(rawData)) {
    normalizedData = rawData.map((item) =>
      normalizePartnerEnums(applyAppReferralLinks(item))
    );
  } else {
    // If data is a single object (e.g., profile), normalize it
    normalizedData = normalizePartnerEnums(
      applyAppReferralLinks(rawData)
    );
  }

  return {
    error: null,
    data: normalizedData,
    statusCode,
    noPartnerProfile: false,
    isUnauthorized: false,
  };
};

const PartnerCommissionApi = {
  getProfile: () => request(`${BASE}/profile`),
  getDashboard: () => request(`${BASE}/dashboard`),
  getReferral: () => request(`${BASE}/referral`),
  getTeam: () => request(`${BASE}/team`),
  createSubPartner: (payload) => request(`${BASE}/team`, "POST", payload),
  updateTeamShare: (childPartnerId, payload) =>
    request(`${BASE}/team/${childPartnerId}/share`, "POST", payload),
  getCommissions: (params = {}) =>
    request(`${BASE}/commissions?${queryString.stringify(params)}`),
  getCommissionDetail: (id) => request(`${BASE}/commissions/${id}`),
  getPayoutSummary: () => request(`${BASE}/payout-summary`),
  requestPayout: (payload) => request(`${BASE}/payouts`, "POST", payload),
  getPayouts: (params = {}) =>
    request(`${BASE}/payouts?${queryString.stringify(params)}`),
  getLeadSummary: () => request(`${BASE}/leads/summary`),
  getLeads: (params = {}) =>
    request(`${BASE}/leads?${queryString.stringify(params)}`),
  getLeadDetail: (leadCode) =>
    request(`${BASE}/leads/detail/${encodeURIComponent(leadCode)}`),
  getReferredProjects: (params = {}) =>
    request(`${BASE}/leads/projects?${queryString.stringify(params)}`),
  getRegisteredWithoutProject: (params = {}) =>
    request(`${BASE}/leads/registered-without-project?${queryString.stringify(params)}`),

  normalize: (res, fallbackMessage) => normalize(res, fallbackMessage),
};

export default PartnerCommissionApi;
